import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import { useRouter } from 'next/router';

import Tabs, { TabPane } from 'rc-tabs';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'antd';
import { useRequest } from 'ahooks';
import ToolTip from '@/components/tooltip';
import Relation from '@/business-components/relation';
import TitleTag from '@/components/titletag';
import 'rc-tabs/assets/index.css';
import UserHead from '@/business-components/userHead';
import ChartHeader from '@/business-components/chart-header';
import LockedCard, { LockCardState } from '@/business-components/LockedCard';
import Chart from '@/business-components/chart';
import { RootState } from '@/models';
import { ConBrandParentCompanyVo, ConParentCompanyProductVo } from '@/services/swagger/data-contracts';
import { ConBrandDetailsWebController } from '@/services/swagger/ConBrandDetailsWebController';
import CustomEmpty from '@/business-components/CustomEmpty';
import { formatNumberValueToChineseUnit } from '@/utils/format';
import SupplyInfoContent from '@/business-components/SupplyInfoContent';
import UnlockConfirmModalContent from '@/business-components/UnlockConfirmModalContent';
import UnlockNumErrorModalContent from '@/business-components/UnlockNumErrorModalContent';
import InvestorScore from '../investorScore';
import styles from './index.module.scss';

const brandDetailController = new ConBrandDetailsWebController();

export type OranizeProps = {
  /** 品牌id */
  conBrandId?: string;
  /** 是否品牌认证方 */
  isBrandVendor?: boolean;
  /** 品牌解锁状态 */
  brandLockState?: string;
  /** 母公司信息 */
  conBrandParentCompanyVo?: ConBrandParentCompanyVo;
  /** 母公司品牌穿透图数据 */
  conParentCompanyProductVo?: ConParentCompanyProductVo;
  onLockedCardStateChange?: (state: string) => void;
};

const Organize: React.FC<OranizeProps> = ({
  conBrandId,
  isBrandVendor,
  brandLockState = 'not_member',
  conBrandParentCompanyVo = {},
  conParentCompanyProductVo = {},
  onLockedCardStateChange,
}) => {
  const dispatch = useDispatch();
  const { token } = useSelector((state: RootState) => state.user);
  const { canUnLockBalanceNum } = useSelector((state: RootState) => state.user.memberInfo);
  /** 组织能力：核心团队信息 */
  const { data: organization } = useRequest(
    async () => {
      const res = await brandDetailController.conBrandDetailsWebControllerV1BrandDetailOrganUsingGet1({
        conBrandId,
      });
      return res.data.data;
    },
    {
      refreshDeps: [isBrandVendor, brandLockState],
    }
  );
  const router = useRouter();
  const ref = useRef();
  const [isMore, setIsMore] = useState(true);
  const viewMore = () => {
    dispatch({
      type: 'modal/open',
      payload: {
        content: (
          <div className={styles.parentDesc}>
            <div className={styles.parentDescContent}>{conBrandParentCompanyVo?.parentCompanyDesc}</div>
          </div>
        ),
        title: '所属公司简介',
      },
    });
  };
  useEffect(() => {
    setIsMore((ref?.current as any)?.offsetHeight < 160);
  }, [conBrandParentCompanyVo?.parentCompanyDesc]);
  const onStateChange = useCallback(
    (state: string) => {
      onLockedCardStateChange?.(state);
    },
    [onLockedCardStateChange]
  );
  const state = useMemo(
    () => (isBrandVendor && !organization?.conBrandGroupList?.length ? 'empty' : brandLockState),
    [brandLockState, isBrandVendor, organization?.conBrandGroupList?.length]
  );
  const backgroundImgUrl = useMemo(
    () =>
      isBrandVendor && !organization?.conBrandGroupList?.length
        ? ''
        : '//assets-oss.cbndata.com/cbndata-refactor-fe/FnWReSbLyFLen9WYur2j_sIsJ13A.png',
    [isBrandVendor, organization?.conBrandGroupList?.length]
  );
  const foregroundImgUrl = useMemo(
    () =>
      isBrandVendor && !organization?.conBrandGroupList?.length
        ? '//assets-oss.cbndata.com/cbndata-refactor-fe/FjOlYZ7nMVM-pyFrZbcUkC3h_Nmy.png'
        : '//assets-oss.cbndata.com/cbndata-refactor-fe/FmMLM1Kied4_R9L7XxLAxzTLx_vN.png',
    [isBrandVendor, organization?.conBrandGroupList?.length]
  );
  const explanation = useMemo(
    () =>
      isBrandVendor && !organization?.conBrandGroupList?.length
        ? '暂未补充核心团队信息'
        : '此处为品牌核心团队成员的个人背景及投资人评分，若想查看该信息，请先开通中国新消费品牌库会员',
    [isBrandVendor, organization?.conBrandGroupList?.length]
  );
  //注册资本
  const parentRegisterAmount = useMemo(() => {
    if (!isNaN(+conBrandParentCompanyVo?.parentRegisterAmount)) {
      return formatNumberValueToChineseUnit(+conBrandParentCompanyVo?.parentRegisterAmount * 1e4, 4);
    }
  }, [conBrandParentCompanyVo?.parentRegisterAmount]);
  const onParentCompanyRateBtnClick = useCallback(() => {
    if (state === 'locked') {
      canUnLockBalanceNum
        ? dispatch({
            type: 'modal/open',
            payload: {
              content: (
                <UnlockConfirmModalContent conBrandId={conBrandId} onUnlocked={() => onStateChange?.('unlocked')} />
              ),
            },
          })
        : dispatch({
            type: 'modal/open',
            payload: {
              content: <UnlockNumErrorModalContent />,
            },
          });
    } else {
      dispatch({
        type: 'modal/open',
        payload: {
          content: <InvestorScore conBrandId={conBrandId} showCompanyScore />,
          title: '投资人评分',
        },
      });
    }
  }, [canUnLockBalanceNum, conBrandId, dispatch, onStateChange, state]);

  const handleCheckStatus = useCallback(
    tabKey => {
      if (!token) {
        dispatch({ type: 'modal/openUserLoginModal', payload: 'login' });
      } else if (state === 'empty') {
        dispatch({
          type: 'modal/open',
          payload: {
            content: <SupplyInfoContent conBrandId={conBrandId} defaultActiveKey={tabKey} />,
            title: '补充信息',
          },
        });
      } else if (state === 'locked') {
        canUnLockBalanceNum
          ? dispatch({
              type: 'modal/open',
              payload: {
                content: (
                  <UnlockConfirmModalContent conBrandId={conBrandId} onUnlocked={() => onStateChange?.('unlocked')} />
                ),
              },
            })
          : dispatch({
              type: 'modal/open',
              payload: {
                content: <UnlockNumErrorModalContent />,
              },
            });
      } else {
        router.push('/member');
      }
    },
    [canUnLockBalanceNum, conBrandId, dispatch, onStateChange, router, state, token]
  );

  const onCoreTeamRateBtnClick = useCallback(() => {
    if (state !== 'unlocked' && state !== 'empty') {
      return handleCheckStatus('coreTeam');
    }
    dispatch({
      type: 'modal/open',
      payload: {
        content: <InvestorScore conBrandId={conBrandId} showGroupScore />,
        title: '投资人评分',
      },
    });
  }, [conBrandId, dispatch, handleCheckStatus, state]);

  return token ? (
    <>
      <ChartHeader
        title={
          <h2 className={styles.moduleTitle}>
            组织能力
            <ToolTip
              title={
                <>
                  <p>由品牌公司实力、核心团队能力等维度搭建的数学模型计算得出。</p>
                  <p>数据来源：天眼查、CBNData数据库、品牌提供。</p>
                </>
              }
              placement="right"
            />
          </h2>
        }
        titleFontSize={18}
        className={styles.header}
      />
      <div className={classnames('flex-row-nowrap', 'flex-between-start', styles.organize, styles.gapStyle)}>
        <Chart
          title="所属公司信息"
          titleFontSize={16}
          dateSubTitle={false}
          panelClassName={styles.leftContent}
          className={styles.title}
          extra={
            <Button type="link" onClick={onParentCompanyRateBtnClick}>
              <svg
                className={classnames('icon', styles.rateIcon)}
                width="13"
                height="13"
                viewBox="0 0 13 13"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.7572 11.8641C4.7572 12.4164 5.20492 12.8641 5.7572 12.8641H6.8232C7.37549 12.8641 7.8232 12.4164 7.8232 11.8641V1.6001C7.8232 1.04781 7.37549 0.600098 6.8232 0.600098H5.7572C5.20492 0.600098 4.7572 1.04781 4.7572 1.6001V11.8641ZM0.158203 11.8641C0.158203 12.4164 0.605918 12.8641 1.1582 12.8641H2.2242C2.77649 12.8641 3.2242 12.4164 3.2242 11.8641V7.7321C3.2242 7.17981 2.77649 6.7321 2.2242 6.7321H1.1582C0.605918 6.7321 0.158203 7.17981 0.158203 7.7321V11.8641ZM10.3562 4.4326C9.80392 4.4326 9.3562 4.88031 9.3562 5.4326V11.8641C9.3562 12.4164 9.80392 12.8641 10.3562 12.8641H11.4222C11.9745 12.8641 12.4222 12.4164 12.4222 11.8641V5.4326C12.4222 4.88031 11.9745 4.4326 11.4222 4.4326H10.3562Z"
                  fill="#F87A55"
                />
              </svg>
              查看评分
            </Button>
          }
        >
          {!conBrandParentCompanyVo || !Object.keys(conBrandParentCompanyVo).length ? (
            <CustomEmpty className={styles.emptyState} />
          ) : (
            <div className={styles.content}>
              <div className={classnames('flex-row-nowrap', 'flex-between-center', styles.title)}>
                <TitleTag style={{ marginBottom: 0 }}>注册资本</TitleTag>
                <div>
                  <span style={{ fontWeight: 500 }}>{parentRegisterAmount?.value}</span>
                  {parentRegisterAmount?.unit}
                </div>
              </div>
              <div className={styles.title}>
                <TitleTag style={{ marginBottom: 0 }}>所属公司简介</TitleTag>
              </div>
              <div>
                <div ref={ref} className={`${isMore ? styles.describeContentMore : styles.describeContent}`}>
                  {conBrandParentCompanyVo?.parentCompanyDesc}
                </div>
                {!isMore && (
                  <div className={styles.more}>
                    <span onClick={viewMore}>显示更多</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </Chart>
        <Chart
          title="核心团队信息"
          titleFontSize={16}
          panelClassName={styles.rightContent}
          className={styles.title}
          extra={
            <Button type="link" onClick={onCoreTeamRateBtnClick}>
              <svg
                className={classnames('icon', styles.rateIcon)}
                width="13"
                height="13"
                viewBox="0 0 13 13"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.7572 11.8641C4.7572 12.4164 5.20492 12.8641 5.7572 12.8641H6.8232C7.37549 12.8641 7.8232 12.4164 7.8232 11.8641V1.6001C7.8232 1.04781 7.37549 0.600098 6.8232 0.600098H5.7572C5.20492 0.600098 4.7572 1.04781 4.7572 1.6001V11.8641ZM0.158203 11.8641C0.158203 12.4164 0.605918 12.8641 1.1582 12.8641H2.2242C2.77649 12.8641 3.2242 12.4164 3.2242 11.8641V7.7321C3.2242 7.17981 2.77649 6.7321 2.2242 6.7321H1.1582C0.605918 6.7321 0.158203 7.17981 0.158203 7.7321V11.8641ZM10.3562 4.4326C9.80392 4.4326 9.3562 4.88031 9.3562 5.4326V11.8641C9.3562 12.4164 9.80392 12.8641 10.3562 12.8641H11.4222C11.9745 12.8641 12.4222 12.4164 12.4222 11.8641V5.4326C12.4222 4.88031 11.9745 4.4326 11.4222 4.4326H10.3562Z"
                  fill="#F87A55"
                />
              </svg>
              查看评分
            </Button>
          }
        >
          {state !== 'unlocked' && state !== 'empty' ? (
            <LockedCard
              conBrandId={conBrandId}
              state={state as LockCardState}
              backgroundImgUrl={backgroundImgUrl}
              foregroundImgUrl={foregroundImgUrl}
              explanation={explanation}
              tabKey="coreTeam"
              className={styles.teamAbilityLockedCard}
              explanationClassName={state === 'empty' ? styles.emptyExplanation : styles.teamAbilityLockedExplanation}
              onStateChange={onStateChange}
            />
          ) : !organization?.conBrandGroupList?.length ? (
            <CustomEmpty className={styles.emptyState} />
          ) : (
            <Tabs tabPosition="left">
              {organization?.conBrandGroupList?.map((item, index) => (
                <TabPane
                  key={index}
                  tab={
                    <UserHead avatarUrl={item?.avatarUrl} memberName={item?.memberName} memberJob={item?.memberJob} />
                  }
                >
                  <div className={styles.tabContent}>
                    <TitleTag style={{ marginBottom: '10px' }}>简介</TitleTag>
                    <div style={{ height: 210, overflowY: 'scroll' }}>{item?.memberDesc}</div>
                  </div>
                </TabPane>
              ))}
            </Tabs>
          )}
        </Chart>
      </div>
      <Chart
        title={
          <h2 className={styles.chartTitle}>
            母公司旗下品牌（已收录）
            <ToolTip title="基于CBNData数据库整理得出与该品牌归属同一家公司的品牌图谱。" placement="right" />
          </h2>
        }
        titleFontSize={16}
        panelClassName={styles.productChart}
        className={styles.title}
      >
        {!conParentCompanyProductVo || !Object.keys(conParentCompanyProductVo).length ? (
          <CustomEmpty className={styles.emptyState} />
        ) : (
          <Relation data={conParentCompanyProductVo} />
        )}
      </Chart>
    </>
  ) : (
    <LockedCard
      conBrandId={conBrandId}
      className={styles.loginLockedCard}
      backgroundImgUrl="//assets-oss.cbndata.com/cbndata-refactor-fe/FnWReSbLyFLen9WYur2j_sIsJ13A.png"
      explanation="此处为品牌各维度的详细数据，若要查看更多数据，请先完成登录"
      explanationClassName={styles.loginLockedExplanation}
      btnContent="登录"
      onStateChange={onStateChange}
    />
  );
};

export default Organize;
